<template>
	<GoalCardSkeleton v-if="showSkeleton" />
	<div
		v-else
		:class="classes"
		@click="onGoalClick"
	>
		<div class="goal-card__sub-group">
			<div class="goal-card__title" :title="title">{{ title }}</div>
			<div class="goal-card__date-row">
				<div
					v-if="showTeamName"
					class="goal-card__team-name">
					{{ teamName }}
				</div>
				<div>
					<TeamingChip
						class-name="goal-card__due-date"
						@click="onGoalClick"
					>
						<template v-slot:text>
							{{ plannedFinishDateForDisplay }}
						</template>
					</TeamingChip>
				</div>
				<div class="goal-card__last-updated">
					{{ translate( "lastUpdatedLabel", { lastUpdated: lastUpdatedForDisplay } ) }}
				</div>
			</div>
		</div>
		<div class="goal-card__sub-group">
			<div class="goal-card__details">
				<div class="goal-card__confidence">
					<div class="goal-card__detail-header">{{ translate( "ConfidenceLabel" ) }}</div>
					<div class="goal-card__detail-cell">
						<ConfidenceChip
							class-name="goal-card__confidence-level-chip"
							:level="confidenceLevel"
							@click="onGoalClick"
						/>
					</div>
				</div>
				<div class="goal-card__kr-count">
					<div class="goal-card__detail-header">{{ translate( "krsLabel" ) }}</div>
					<div class="goal-card__detail-cell">{{ keyResultCount }}</div>
				</div>
				<div class="goal-card__progress">
					<div class="goal-card__detail-header">{{ translate( "ProgressLabel" ) }}</div>
					<div class="goal-card__detail-cell">
						<TeamingProgressBar
							:percent-completed="percentCompleted"
							:percent-progressed="percentProgressed"
							:show-percent="true"
							percent-position="LEFT"
						/>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import moment from "moment-timezone";

import {
	TranslateMixin,
	TeamingChip,
	TeamingProgressBar,
	ConfidenceChip
} from "@teaming/common-ui";

import GoalCardSkeleton from "./GoalCardSkeleton";

export default {
	name: "GoalCard",
	teamingPackage: "common-gokr",
	mixins: [ TranslateMixin, ],
	components: {
		GoalCardSkeleton,
		TeamingChip,
		TeamingProgressBar,
		ConfidenceChip,
	},
	emits: [ "goalSelected", ],
	props: {
		className: {
			type: String,
			default: "",
		},
		goal: {
			type: Object,
			default: () => ( {} ),
		},
		showTeamName: {
			type: Boolean,
			default: false,
		},
		showSkeleton: {
			type: Boolean,
			default: false,
		},
	},
	computed: {
		classes() {
			return {
				"goal-card": true,
				[ this.className ]: !!this.className,
			};
		},
		id() {
			return this.goal.id;
		},
		title() {
			return this.goal.title;
		},
		teamName() {
			const { goal, } = this;
			const { teamName = "", extendedTeamName = "", } = goal;
			return extendedTeamName || teamName;
		},
		percentCompleted() {
			return this.goal.percentCompleted;
		},
		percentProgressed() {
			return this.goal.percentProgressed;
		},
		confidenceLevel() {
			return this.goal.confidence;
		},
		plannedFinishDateForDisplay() {
			return this.goal.plannedFinishDate.format( "MMM D" );
		},
		lastUpdatedForDisplay() {
			return this.goal.lastUpdated.from( moment() );
		},
		keyResultCount() {
			return this.goal.keyResults?.length || 0;
		},
	},
	methods: {
		onGoalClick( e ) {
			this.$emit( "goalSelected", e, this.id );
		},
	},
};
</script>

<style lang="scss" scoped>
$goalCardBackgroundColor: $white;
$goalCardBorderColor: $color1-200;
$goalCardBorderColor--hover: $color2-500;
$goalCardTitleFontColor: $color1-700;
$goalCardPercentFontColor: $color2-500;
$goalCardDetailHeaderFontColor: $color1-500;
$goalCardKRCountFontColor: $color1-700;
$goalCardTeamNameFontColor: $color2-600;
$goalCardTeamNameBackgroundColor: $color2-100;
$goalCardDueDateFontColor: $color1-600;
$goalCardDueDateBackgroundColor: $color1-100;
$goalCardLastUpdatedFontColor: $color1-500;

.goal-card {
	@include font-and-line-for(1);
	display: flex;
	align-items: flex-start;
	justify-content: flex-start;
	padding: 1rem;
	background: $goalCardBackgroundColor;
	box-shadow: 0 0.0625rem .25rem rgba(42, 47, 69, 0.1);
	border-radius: 0.5rem;
	position: relative;
	margin-bottom: 1rem;
	border: 0.0625rem solid $goalCardBorderColor;

	&:hover {
		border: 0.0625rem solid $goalCardBorderColor--hover;
	}

	@include for-size('xs') {
		flex-direction: column;
		max-height: 9.25rem;
	}

	@include for-size('sm') {
		flex-direction: column;
		max-height: 9.25rem;
	}

	@include for-size('md') {
		flex-direction: row;
		align-items: flex-end;
		max-height: 5.25rem;
	}

	@include for-size('lg') {
		flex-direction: row;
		align-items: flex-end;
		max-height: 5.25rem;
	}

	@include for-size('xl') {
		flex-direction: row;
		align-items: flex-end;
		max-height: 5.25rem;
	}
}

.goal-card__sub-group {
	@include for-size('xs') {
		width: 100%;
	}

	@include for-size('sm') {
		width: 100%;
	}

	@include for-size('md') {
		width: 50%;
	}

	@include for-size('lg') {
		width: 50%;
	}

	@include for-size('xl') {
		width: 50%;
	}
}

.goal-card__title {
	font-weight: 600;
	color: $goalCardTitleFontColor;
	margin-bottom: .5rem;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	width: 100%;
	cursor: pointer;
	text-align: left;
}

.goal-card__date-row {
	display: flex;
	align-items: baseline;
	margin-bottom: 1rem;

	@include for-size('md') {
		margin-bottom: 0;
	}

	@include for-size('lg') {
		margin-bottom: 0;
	}

	@include for-size('xl') {
		margin-bottom: 0;
	}
}

.goal-card__team-name {
	@include font-and-line-for(-1);
	font-weight: 500;
	color: $goalCardTeamNameFontColor;
	background: $goalCardTeamNameBackgroundColor;
	margin-right: .5rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
	padding: .125rem .375rem .125rem .375rem;
    border-radius: .25rem;
	max-width: 10rem;

	@include for-size('xs') {
		max-width: 8rem;
	}
}

.goal-card__due-date {
	@include font-and-line-for(-1);
	font-weight: 500;
	color: $goalCardDueDateFontColor;
	background: $goalCardDueDateBackgroundColor;
	margin-right: .5rem;
	cursor: default;
}

.goal-card__last-updated {
	@include font-and-line-for(-1);
	font-weight: 600;
	color: $goalCardLastUpdatedFontColor;
}

.goal-card__details {
	display: flex;
	flex-direction: row;
	justify-content: space-between;
	width: 100%;

	> div:not(:last-of-type) {
		margin-right: 1.5rem;
	}
}

.goal-card__detail-header {
	text-align: left;
	@include font-and-line-for(-1);
	font-weight: 600;
	color: $goalCardDetailHeaderFontColor;
	margin-bottom: .5rem;
}

.goal-card__detail-cell {
	text-align: left;
}

.goal-card__kr-count {
	.goal-card__detail-cell {
		@include font-and-line-for(0);
		font-weight: 600;
		color: $goalCardKRCountFontColor;
	}
}

.goal-card__progress {
	flex-grow: 1;
	.goal-card__detail-cell {
		@include font-and-line-for(1);
		font-weight: 600;
		color: $goalCardPercentFontColor;
	}
}

.goal-card__confidence {
	flex-basis: 1;
}

.goal-card__confidence-level-chip {
	&.teaming-chip {
		cursor: default;
	}
}

</style>
